import React, { useEffect, useMemo, useState } from 'react';
import './EtlBisModule.css';

interface EtlStatus {
  ultimaExecucao: string | null;
  proximaExecucao: string | null;
  totalJobs: number;
  activeJobs: number;
  pausedJobs: number;
  runningJobs: number;
  jobsWithError: number;
  jobsExecuted: number;
  jobsDelayed: number;
  status: string;
}

interface EtlJob {
  id: number;
  nm_job: string;
  ds_job: string | null;
  ds_query_origem?: string | null;
  cron_expr: string | null;
  nr_limite_carga_inicial?: number;
  nm_coluna_dt_criacao_origem?: string | null;
  nm_coluna_dt_atualizacao_origem?: string | null;
  nm_coluna_dt_referencia_destino?: string | null;
  criar_tabela_destino_se_nao_existir?: boolean;
  nm_tabela_destino?: string | null;
  mapeamento_colunas?: ColumnMapping[];
  colunas_chave_destino?: string[];
  cd_ativo: number;
  st_job?: string;
  conexao_origem?: DbConnectionConfig;
  conexao_destino?: DbConnectionConfig;
}

interface EtlExecution {
  id: number;
  job_id: number;
  nm_job: string;
  st_execucao: string;
  dt_inicio: string | null;
  dt_fim: string | null;
  ds_erro: string | null;
  ds_query_executada?: string | null;
  ds_resultado_consulta?: string | ExecutionResultSummary | null;
  nr_registros_lidos?: number;
  nr_registros_processados: number;
  nr_registros_inseridos?: number;
  nr_registros_atualizados?: number;
  nr_registros_rejeitados?: number;
  criado_por: string | null;
}

interface ExecutionSummaryColumn {
  coluna: string;
  tipo: string;
  contagem: number;
  nulos: number;
  exemplo: string;
}

interface ExecutionResultSummary {
  totalRegistrosLidos?: number;
  totalRegistrosElegiveis?: number;
  totalRegistrosProcessados?: number;
  totalRegistrosInseridos?: number;
  totalRegistrosAtualizados?: number;
  totalRegistrosRejeitados?: number;
  filtrosAplicados?: {
    var_ULT_ATUALIZACAO?: string;
    possuiWatermarkDestino?: boolean;
    colunaCriacaoOrigem?: string | null;
    colunaAtualizacaoOrigem?: string | null;
    colunaReferenciaDestino?: string | null;
    tabelaDestino?: string | null;
    limiteCargaInicial?: number;
  };
  colunas?: ExecutionSummaryColumn[];
}

interface DbConnectionConfig {
  tipo: string;
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
}

interface ColumnMapping {
  sourceColumn: string;
  sourceType: string;
  inferredDestinationType: string;
  keepName: boolean;
  destinationColumn: string;
  destinationType: string;
}

interface DestinationColumn {
  name: string;
  type: string;
}

interface JobFormData {
  nm_job: string;
  ds_job: string;
  ds_query_origem: string;
  cron_expr: string;
  nr_limite_carga_inicial: number;
  nm_coluna_dt_criacao_origem: string;
  nm_coluna_dt_atualizacao_origem: string;
  nm_coluna_dt_referencia_destino: string;
  colunas_chave_destino: string;
  criar_tabela_destino_se_nao_existir: boolean;
  nm_tabela_destino: string;
  mapeamento_colunas: ColumnMapping[];
  conexao_origem: DbConnectionConfig;
  conexao_destino: DbConnectionConfig;
}

const DB_TYPE_OPTIONS = [
  { value: 'mysql', label: 'MySQL' },
  { value: 'sqlserver', label: 'SQL Server' },
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'oracle', label: 'Oracle' },
  { value: 'sqlite', label: 'SQLite' },
  { value: 'api', label: 'API' },
  { value: 'other', label: 'Outro' }
];

const formatDateTime = (value: string | null) => {
  if (!value) return '-';

  const mysqlLikeMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (mysqlLikeMatch) {
    const [, yyyy, mm, dd, hh, mi, ss] = mysqlLikeMatch;
    return `${dd}/${mm}/${yyyy}, ${hh}:${mi}:${ss || '00'}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
};

const formatCronReadable = (cronExpr: string | null | undefined) => {
  if (!cronExpr) return '-';

  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length !== 5) return cronExpr;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  if (hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && /^\d+$/.test(minute)) {
    const everyHours = Number(hour.replace('*/', ''));
    if (Number.isFinite(everyHours) && everyHours > 0) {
      const mm = String(Number(minute)).padStart(2, '0');
      if (everyHours === 1) {
        return mm === '00' ? 'A cada 1 hora' : `A cada 1 hora, no minuto ${mm}`;
      }
      return mm === '00'
        ? `A cada ${everyHours} horas`
        : `A cada ${everyHours} horas, no minuto ${mm}`;
    }
  }

  if (hour === '*' && minute.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const everyMinutes = Number(minute.replace('*/', ''));
    if (Number.isFinite(everyMinutes) && everyMinutes > 0) {
      return everyMinutes === 1 ? 'A cada 1 minuto' : `A cada ${everyMinutes} minutos`;
    }
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const hh = String(Number(hour)).padStart(2, '0');
    const mm = String(Number(minute)).padStart(2, '0');
    return `Todos os dias às ${hh}:${mm}`;
  }

  return cronExpr;
};

const parseCronField = (field: string, min: number, max: number) => {
  const trimmed = String(field || '').trim();
  if (!trimmed || trimmed === '*') {
    return (_value: number) => true;
  }

  if (trimmed.startsWith('*/')) {
    const step = Number(trimmed.slice(2));
    if (!Number.isFinite(step) || step <= 0) {
      return (_value: number) => false;
    }

    return (value: number) => value >= min && value <= max && value % step === 0;
  }

  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric)) {
    return (_value: number) => false;
  }

  return (value: number) => value === numeric;
};

const getNextRunFromCron = (cronExpr: string | null | undefined, nowDate = new Date()) => {
  if (!cronExpr) return null;

  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  const [minuteField, hourField, dayField, monthField, weekDayField] = parts;
  const minuteMatch = parseCronField(minuteField, 0, 59);
  const hourMatch = parseCronField(hourField, 0, 23);
  const dayMatch = parseCronField(dayField, 1, 31);
  const monthMatch = parseCronField(monthField, 1, 12);
  const weekDayMatch = parseCronField(weekDayField, 0, 6);

  const start = new Date(nowDate.getTime());
  start.setSeconds(0, 0);
  start.setMinutes(start.getMinutes() + 1);

  const maxLookAheadMinutes = 60 * 24 * 14;
  for (let i = 0; i <= maxLookAheadMinutes; i += 1) {
    const candidate = new Date(start.getTime() + i * 60 * 1000);
    const isMatch = minuteMatch(candidate.getMinutes())
      && hourMatch(candidate.getHours())
      && dayMatch(candidate.getDate())
      && monthMatch(candidate.getMonth() + 1)
      && weekDayMatch(candidate.getDay());

    if (isMatch) {
      return candidate;
    }
  }

  return null;
};

const parseDateToTimestamp = (value: string | null | undefined) => {
  if (!value) return 0;

  const mysqlLikeMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (mysqlLikeMatch) {
    const [, yyyy, mm, dd, hh, mi, ss] = mysqlLikeMatch;
    const date = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss || '0')
    );

    const ts = date.getTime();
    return Number.isFinite(ts) ? ts : 0;
  }

  const ts = new Date(String(value)).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const getNextRunForDisplay = (
  cronExpr: string | null | undefined,
  _lastExecutionAt: string | null | undefined,
  nowDate = new Date()
) => getNextRunFromCron(cronExpr, nowDate);

const formatRemainingTime = (targetDate: Date | null, nowDate = new Date()) => {
  if (!targetDate) return '-';

  const diffMs = targetDate.getTime() - nowDate.getTime();
  if (diffMs <= 0) return 'agora';

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}min`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }

  return `${minutes}min`;
};

const getExecutionTimestamp = (execution: EtlExecution | undefined) => execution?.dt_fim || execution?.dt_inicio || null;

const parseExecutionSummary = (rawValue: unknown): ExecutionResultSummary | null => {
  if (!rawValue) return null;

  if (typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    return rawValue as ExecutionResultSummary;
  }

  if (typeof rawValue !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as ExecutionResultSummary;
    }
  } catch (_error) {
    return null;
  }

  return null;
};

const createEmptyConnection = (tipo = 'mysql'): DbConnectionConfig => ({
  tipo,
  host: '',
  port: '',
  database: '',
  user: '',
  password: ''
});

const createEmptyJobForm = (): JobFormData => ({
  nm_job: '',
  ds_job: '',
  ds_query_origem: 'SELECT * FROM tabela_origem LIMIT 1000',
  cron_expr: '0 */2 * * *',
  nr_limite_carga_inicial: 100,
  nm_coluna_dt_criacao_origem: 'dt_criacao',
  nm_coluna_dt_atualizacao_origem: 'dt_atualizacao',
  nm_coluna_dt_referencia_destino: 'dt_referencia',
  colunas_chave_destino: '',
  criar_tabela_destino_se_nao_existir: false,
  nm_tabela_destino: 'etl_destino',
  mapeamento_colunas: [],
  conexao_origem: createEmptyConnection('mysql'),
  conexao_destino: createEmptyConnection('sqlserver')
});

const EtlBisModule: React.FC = () => {
  const [status, setStatus] = useState<EtlStatus | null>(null);
  const [jobs, setJobs] = useState<EtlJob[]>([]);
  const [executions, setExecutions] = useState<EtlExecution[]>([]);
  const [loading, setLoading] = useState(false);
  const [runningJobId, setRunningJobId] = useState<number | null>(null);
  const [changingJobId, setChangingJobId] = useState<number | null>(null);
  const [clockTick, setClockTick] = useState<number>(Date.now());
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'info' | 'success' | 'error'>('info');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobSort, setJobSort] = useState<'name_asc' | 'name_desc' | 'recent_execution' | 'delay_first'>('name_asc');
  const [executionStatusFilter, setExecutionStatusFilter] = useState<'all' | 'queued' | 'running' | 'success' | 'error'>('all');
  const [executionSort, setExecutionSort] = useState<'recent' | 'oldest' | 'delay_first' | 'records_desc'>('recent');
  const [expandedExecutionId, setExpandedExecutionId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formJobId, setFormJobId] = useState<number | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [jobForm, setJobForm] = useState<JobFormData>(createEmptyJobForm());
  const [testingConnectionSide, setTestingConnectionSide] = useState<'conexao_origem' | 'conexao_destino' | null>(null);
  const [connectionTestFeedback, setConnectionTestFeedback] = useState<{
    conexao_origem: { type: 'idle' | 'success' | 'error'; message: string };
    conexao_destino: { type: 'idle' | 'success' | 'error'; message: string };
  }>({
    conexao_origem: { type: 'idle', message: '' },
    conexao_destino: { type: 'idle', message: '' }
  });
  const [sourceColumnsLoading, setSourceColumnsLoading] = useState(false);
  const [destinationColumnsLoading, setDestinationColumnsLoading] = useState(false);
  const [destinationTableExists, setDestinationTableExists] = useState<boolean | null>(null);
  const [destinationColumns, setDestinationColumns] = useState<DestinationColumn[]>([]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [statusResponse, jobsResponse, executionsResponse] = await Promise.all([
        fetch('/api/etl-bis/status'),
        fetch('/api/etl-bis/jobs'),
        fetch('/api/etl-bis/executions?limit=25')
      ]);

      if (!statusResponse.ok || !jobsResponse.ok || !executionsResponse.ok) {
        throw new Error('Falha ao consultar dados do ETL BIs');
      }

      const [statusData, jobsData, executionsData] = await Promise.all([
        statusResponse.json(),
        jobsResponse.json(),
        executionsResponse.json()
      ]);

      setStatus(statusData);
      setJobs(Array.isArray(jobsData) ? jobsData : []);
      setExecutions(Array.isArray(executionsData) ? executionsData : []);
      setLastUpdatedAt(new Date());
      if (messageType !== 'success') {
        setMessage('');
      }
    } catch (error) {
      console.error(error);
      setStatus({
        ultimaExecucao: null,
        proximaExecucao: null,
        totalJobs: 0,
        activeJobs: 0,
        pausedJobs: 0,
        runningJobs: 0,
        jobsWithError: 0,
        jobsExecuted: 0,
        jobsDelayed: 0,
        status: 'Indisponivel'
      });
      setJobs([]);
      setExecutions([]);
      setMessageType('error');
      setMessage('Nao foi possivel consultar os dados do ETL BIs.');
    } finally {
      setLoading(false);
    }
  };

  const executarJob = async (jobId: number) => {
    try {
      setRunningJobId(jobId);
      setMessageType('info');
      setMessage('Disparando job...');

      const response = await fetch(`/api/etl-bis/jobs/${jobId}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ requestedBy: 'frontend' })
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMessageType('error');
        setMessage(payload.error || 'Nao foi possivel disparar o job.');
        return;
      }

      setMessageType('success');
      setMessage('Job agendado com sucesso. Atualizando execucoes...');
      await carregarDados();
    } catch (error) {
      console.error(error);
      setMessageType('error');
      setMessage('Erro ao disparar job.');
    } finally {
      setRunningJobId(null);
    }
  };

  const abrirCriacaoJob = () => {
    setFormMode('create');
    setFormJobId(null);
    setJobForm(createEmptyJobForm());
    setConnectionTestFeedback({
      conexao_origem: { type: 'idle', message: '' },
      conexao_destino: { type: 'idle', message: '' }
    });
    setDestinationTableExists(null);
    setDestinationColumns([]);
    setIsFormOpen(true);
  };

  const abrirEdicaoJob = (job: EtlJob) => {
    setFormMode('edit');
    setFormJobId(job.id);
    setJobForm({
      nm_job: job.nm_job || '',
      ds_job: job.ds_job || '',
      ds_query_origem: job.ds_query_origem || '',
      cron_expr: job.cron_expr || '',
      nr_limite_carga_inicial: Number(job.nr_limite_carga_inicial || 100),
      nm_coluna_dt_criacao_origem: job.nm_coluna_dt_criacao_origem || '',
      nm_coluna_dt_atualizacao_origem: job.nm_coluna_dt_atualizacao_origem || '',
      nm_coluna_dt_referencia_destino: job.nm_coluna_dt_referencia_destino || '',
      colunas_chave_destino: Array.isArray(job.colunas_chave_destino) ? job.colunas_chave_destino.join(', ') : '',
      criar_tabela_destino_se_nao_existir: Boolean(job.criar_tabela_destino_se_nao_existir),
      nm_tabela_destino: job.nm_tabela_destino || '',
      mapeamento_colunas: Array.isArray(job.mapeamento_colunas) ? job.mapeamento_colunas : [],
      conexao_origem: {
        tipo: job.conexao_origem?.tipo || 'mysql',
        host: job.conexao_origem?.host || '',
        port: job.conexao_origem?.port || '',
        database: job.conexao_origem?.database || '',
        user: job.conexao_origem?.user || '',
        password: job.conexao_origem?.password || ''
      },
      conexao_destino: {
        tipo: job.conexao_destino?.tipo || 'sqlserver',
        host: job.conexao_destino?.host || '',
        port: job.conexao_destino?.port || '',
        database: job.conexao_destino?.database || '',
        user: job.conexao_destino?.user || '',
        password: job.conexao_destino?.password || ''
      }
    });
    setConnectionTestFeedback({
      conexao_origem: { type: 'idle', message: '' },
      conexao_destino: { type: 'idle', message: '' }
    });
    setDestinationTableExists(null);
    setDestinationColumns([]);
    setIsFormOpen(true);
  };

  const alterarCampoForm = (
    field:
      | 'nm_job'
      | 'ds_job'
      | 'ds_query_origem'
      | 'cron_expr'
      | 'nm_tabela_destino'
      | 'nm_coluna_dt_criacao_origem'
      | 'nm_coluna_dt_atualizacao_origem'
      | 'nm_coluna_dt_referencia_destino'
      | 'colunas_chave_destino',
    value: string
  ) => {
    setJobForm(previous => ({
      ...previous,
      [field]: value
    }));
  };

  const alterarLimiteCargaInicial = (value: string) => {
    const parsed = Number(value);
    setJobForm(previous => ({
      ...previous,
      nr_limite_carga_inicial: Number.isFinite(parsed) && parsed > 0 ? parsed : 100
    }));
  };

  const alterarFlagCriacaoTabelaDestino = (value: boolean) => {
    setJobForm(previous => ({
      ...previous,
      criar_tabela_destino_se_nao_existir: value
    }));
  };

  const alterarCampoConexao = (
    side: 'conexao_origem' | 'conexao_destino',
    field: keyof DbConnectionConfig,
    value: string
  ) => {
    setJobForm(previous => ({
      ...previous,
      [side]: {
        ...previous[side],
        [field]: value
      }
    }));
  };

  const atualizarMapping = (index: number, field: keyof ColumnMapping, value: string | boolean) => {
    setJobForm(previous => {
      const mappings = [...previous.mapeamento_colunas];
      if (!mappings[index]) return previous;

      const current = { ...mappings[index], [field]: value } as ColumnMapping;
      if (field === 'keepName' && value === true) {
        current.destinationColumn = current.sourceColumn;
      }

      mappings[index] = current;
      return {
        ...previous,
        mapeamento_colunas: mappings
      };
    });
  };

  const carregarColunasOrigem = async () => {
    try {
      setSourceColumnsLoading(true);
      setMessageType('info');
      setMessage('Lendo colunas a partir da query de origem...');

      const response = await fetch('/api/etl-bis/metadata/source-columns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connection: jobForm.conexao_origem,
          query: jobForm.ds_query_origem
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessageType('error');
        setMessage(payload.error || 'Nao foi possivel obter colunas da origem.');
        return;
      }

      const sourceMappings = Array.isArray(payload.columns) ? payload.columns : [];
      setJobForm(previous => ({ ...previous, mapeamento_colunas: sourceMappings }));
      setMessageType('success');
      setMessage(`${sourceMappings.length} coluna(s) carregada(s) da origem.`);
    } catch (error) {
      console.error(error);
      setMessageType('error');
      setMessage('Erro ao ler colunas da origem.');
    } finally {
      setSourceColumnsLoading(false);
    }
  };

  const carregarColunasDestino = async () => {
    try {
      setDestinationColumnsLoading(true);
      setMessageType('info');
      setMessage('Verificando colunas da tabela de destino...');

      const response = await fetch('/api/etl-bis/metadata/destination-columns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connection: jobForm.conexao_destino,
          tableName: jobForm.nm_tabela_destino,
          mappings: jobForm.mapeamento_colunas,
          options: {
            createTableIfMissing: jobForm.criar_tabela_destino_se_nao_existir
          }
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setDestinationTableExists(null);
        setDestinationColumns([]);
        setMessageType('error');
        setMessage(payload.error || 'Nao foi possivel obter colunas da tabela destino.');
        return;
      }

      const exists = Boolean(payload.tableExists);
      const columns = Array.isArray(payload.columns) ? payload.columns : [];
      setDestinationTableExists(exists);
      setDestinationColumns(columns);

      setJobForm(previous => {
        const updatedMappings = previous.mapeamento_colunas.map(item => {
          const matched = columns.find((col: DestinationColumn) => col.name === item.destinationColumn || col.name === item.sourceColumn);
          if (!matched) return item;
          return {
            ...item,
            destinationType: item.destinationType || matched.type
          };
        });

        return {
          ...previous,
          mapeamento_colunas: updatedMappings
        };
      });

      setMessageType('success');
      setMessage(exists
        ? `Tabela '${payload.tableName}' encontrada com ${columns.length} coluna(s).`
        : `Tabela '${payload.tableName}' nao existe no destino.`);
    } catch (error) {
      console.error(error);
      setDestinationTableExists(null);
      setDestinationColumns([]);
      setMessageType('error');
      setMessage('Erro ao consultar tabela de destino.');
    } finally {
      setDestinationColumnsLoading(false);
    }
  };

  const validarForm = () => {
    if (!jobForm.nm_job.trim()) {
      return 'Informe o nome do job.';
    }

    if (!jobForm.ds_query_origem.trim()) {
      return 'Informe a query de origem.';
    }

    if (!jobForm.nm_tabela_destino.trim()) {
      return 'Informe o nome da tabela de destino.';
    }

    if (!jobForm.nm_coluna_dt_criacao_origem.trim() && !jobForm.nm_coluna_dt_atualizacao_origem.trim()) {
      return 'Informe ao menos uma coluna de controle de data na origem.';
    }

    if (!jobForm.nm_coluna_dt_referencia_destino.trim()) {
      return 'Informe a coluna de referencia de data no destino.';
    }

    if (!jobForm.colunas_chave_destino.trim()) {
      return 'Informe ao menos uma coluna chave de destino para evitar duplicidade.';
    }

    if (!jobForm.mapeamento_colunas.length) {
      return 'Carregue as colunas da origem para montar o mapeamento.';
    }

    const validateConnection = (connection: DbConnectionConfig, label: string) => {
      if (!connection.tipo.trim()) return `${label}: selecione o tipo de banco.`;
      if (!connection.host.trim()) return `${label}: informe o host.`;
      if (!connection.database.trim()) return `${label}: informe o database.`;
      if (!connection.user.trim()) return `${label}: informe o usuario.`;
      return null;
    };

    const sourceError = validateConnection(jobForm.conexao_origem, 'Conexao de origem');
    if (sourceError) return sourceError;

    const targetError = validateConnection(jobForm.conexao_destino, 'Conexao de destino');
    if (targetError) return targetError;

    return null;
  };

  const salvarJob = async () => {
    const validationError = validarForm();
    if (validationError) {
      setMessageType('error');
      setMessage(validationError);
      return;
    }

    try {
      setFormSubmitting(true);
      setMessageType('info');
      setMessage(formMode === 'create' ? 'Criando job...' : 'Salvando alteracoes do job...');

      const endpoint = formMode === 'create'
        ? '/api/etl-bis/jobs'
        : `/api/etl-bis/jobs/${formJobId}`;

      const method = formMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nm_job: jobForm.nm_job,
          ds_job: jobForm.ds_job,
          ds_query_origem: jobForm.ds_query_origem,
          cron_expr: jobForm.cron_expr,
          nr_limite_carga_inicial: jobForm.nr_limite_carga_inicial,
          nm_coluna_dt_criacao_origem: jobForm.nm_coluna_dt_criacao_origem,
          nm_coluna_dt_atualizacao_origem: jobForm.nm_coluna_dt_atualizacao_origem,
          nm_coluna_dt_referencia_destino: jobForm.nm_coluna_dt_referencia_destino,
          colunas_chave_destino: jobForm.colunas_chave_destino,
          criar_tabela_destino_se_nao_existir: jobForm.criar_tabela_destino_se_nao_existir,
          nm_tabela_destino: jobForm.nm_tabela_destino,
          mapeamento_colunas: jobForm.mapeamento_colunas,
          conexao_origem: jobForm.conexao_origem,
          conexao_destino: jobForm.conexao_destino
        })
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMessageType('error');
        setMessage(payload.error || 'Nao foi possivel salvar o job.');
        return;
      }

      setMessageType('success');
      setMessage(formMode === 'create' ? 'Novo job criado com sucesso.' : 'Job atualizado com sucesso.');
      setIsFormOpen(false);
      setFormJobId(null);
      setJobForm(createEmptyJobForm());
      await carregarDados();
    } catch (error) {
      console.error(error);
      setMessageType('error');
      setMessage('Erro ao salvar job.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const fecharFormulario = () => {
    if (formSubmitting) return;
    setIsFormOpen(false);
    setFormJobId(null);
    setJobForm(createEmptyJobForm());
    setConnectionTestFeedback({
      conexao_origem: { type: 'idle', message: '' },
      conexao_destino: { type: 'idle', message: '' }
    });
    setDestinationTableExists(null);
    setDestinationColumns([]);
  };

  const testarConexao = async (side: 'conexao_origem' | 'conexao_destino') => {
    const sideLabel = side === 'conexao_origem' ? 'origem' : 'destino';
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    try {
      setTestingConnectionSide(side);
      setMessageType('info');
      setMessage(`Testando conexao de ${sideLabel}...`);
      setConnectionTestFeedback(previous => ({
        ...previous,
        [side]: { type: 'idle', message: '' }
      }));

      const response = await fetch('/api/etl-bis/connections/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal,
        body: JSON.stringify({
          connection: jobForm[side],
          options: {
            createTableIfNotExists: side === 'conexao_destino' && jobForm.criar_tabela_destino_se_nao_existir,
            targetTableName: side === 'conexao_destino' ? jobForm.nm_tabela_destino : ''
          }
        })
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage = payload.error || `Falha ao testar conexao de ${sideLabel}.`;
        setMessageType('error');
        setMessage(errorMessage);
        setConnectionTestFeedback(previous => ({
          ...previous,
          [side]: { type: 'error', message: errorMessage }
        }));
        return;
      }

      const successMessage = payload.message || `Conexao de ${sideLabel} validada com sucesso.`;
      setMessageType('success');
      setMessage(successMessage);
      setConnectionTestFeedback(previous => ({
        ...previous,
        [side]: { type: 'success', message: successMessage }
      }));
    } catch (error) {
      console.error(error);
      const timeoutError = error instanceof DOMException && error.name === 'AbortError';
      const errorMessage = timeoutError
        ? `Timeout ao testar conexao de ${sideLabel} (12s).`
        : `Erro ao testar conexao de ${sideLabel}.`;
      setMessageType('error');
      setMessage(errorMessage);
      setConnectionTestFeedback(previous => ({
        ...previous,
        [side]: { type: 'error', message: errorMessage }
      }));
    } finally {
      clearTimeout(timeoutId);
      setTestingConnectionSide(null);
    }
  };

  const alterarEstadoJob = async (job: EtlJob, action: 'pause' | 'resume' | 'deactivate') => {
    try {
      setChangingJobId(job.id);
      setMessageType('info');
      setMessage(
        action === 'deactivate'
          ? `Desativando job ${job.nm_job}...`
          : action === 'pause'
            ? `Pausando job ${job.nm_job}...`
            : `Retomando job ${job.nm_job}...`
      );

      const response = await fetch(`/api/etl-bis/jobs/${job.id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessageType('error');
        setMessage(payload.error || 'Nao foi possivel alterar o estado do job.');
        return;
      }

      setMessageType('success');
      setMessage(payload.message || 'Estado do job atualizado com sucesso.');
      await carregarDados();
    } catch (error) {
      console.error(error);
      setMessageType('error');
      setMessage('Erro ao alterar estado do job.');
    } finally {
      setChangingJobId(null);
    }
  };

  const pausarJob = (job: EtlJob) => {
    const isPaused = String(job.st_job || 'active').toLowerCase() === 'paused';
    alterarEstadoJob(job, isPaused ? 'resume' : 'pause');
  };

  const desativarJob = (job: EtlJob) => {
    const confirmed = window.confirm(`Deseja realmente desativar o job ${job.nm_job}?`);
    if (!confirmed) return;
    alterarEstadoJob(job, 'deactivate');
  };

  const resolveJobsExecuted = () => {
    if (status && typeof status.jobsExecuted === 'number') {
      return status.jobsExecuted;
    }

    if (!executions.length) {
      return 0;
    }

    return executions.filter(item => String(item.st_execucao).toLowerCase() === 'success').length;
  };

  const parseDateValue = (value: string | null) => parseDateToTimestamp(value);

  const isExecutionDelayed = (execution: EtlExecution) => {
    const start = parseDateValue(execution.dt_inicio);
    const end = parseDateValue(execution.dt_fim);

    if (!start || !end || end <= start) {
      return false;
    }

    const durationMs = end - start;
    return durationMs > 10 * 60 * 1000;
  };

  const lastExecutionByJob = useMemo(() => {
    const map = new Map<number, EtlExecution>();
    executions.forEach(execution => {
      const current = map.get(execution.job_id);
      if (!current || execution.id > current.id) {
        map.set(execution.job_id, execution);
      }
    });
    return map;
  }, [executions]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredJobs = useMemo(() => {
    const filtered = jobs.filter(job => {
      if (!normalizedSearch) {
        return true;
      }

      const searchable = `${job.nm_job} ${job.ds_job || ''} ${job.cron_expr || ''}`.toLowerCase();
      return searchable.includes(normalizedSearch);
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (jobSort === 'name_asc') {
        return a.nm_job.localeCompare(b.nm_job, 'pt-BR');
      }

      if (jobSort === 'name_desc') {
        return b.nm_job.localeCompare(a.nm_job, 'pt-BR');
      }

      if (jobSort === 'recent_execution') {
        const aLast = lastExecutionByJob.get(a.id);
        const bLast = lastExecutionByJob.get(b.id);
        const aTime = aLast ? Math.max(parseDateValue(aLast.dt_fim), parseDateValue(aLast.dt_inicio)) : 0;
        const bTime = bLast ? Math.max(parseDateValue(bLast.dt_fim), parseDateValue(bLast.dt_inicio)) : 0;
        return bTime - aTime;
      }

      const aDelayed = lastExecutionByJob.get(a.id) ? isExecutionDelayed(lastExecutionByJob.get(a.id) as EtlExecution) : false;
      const bDelayed = lastExecutionByJob.get(b.id) ? isExecutionDelayed(lastExecutionByJob.get(b.id) as EtlExecution) : false;

      if (aDelayed !== bDelayed) {
        return aDelayed ? -1 : 1;
      }

      return a.nm_job.localeCompare(b.nm_job, 'pt-BR');
    });

    return sorted;
  }, [jobs, normalizedSearch, jobSort, lastExecutionByJob]);

  const filteredExecutions = useMemo(() => {
    const filtered = executions.filter(execution => {
      const statusOk = executionStatusFilter === 'all'
        ? true
        : String(execution.st_execucao).toLowerCase() === executionStatusFilter;

      if (!statusOk) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchable = `${execution.nm_job} ${execution.st_execucao} ${execution.criado_por || ''}`.toLowerCase();
      return searchable.includes(normalizedSearch);
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (executionSort === 'oldest') {
        return a.id - b.id;
      }

      if (executionSort === 'records_desc') {
        return Number(b.nr_registros_processados || 0) - Number(a.nr_registros_processados || 0);
      }

      if (executionSort === 'delay_first') {
        const aDelayed = isExecutionDelayed(a);
        const bDelayed = isExecutionDelayed(b);

        if (aDelayed !== bDelayed) {
          return aDelayed ? -1 : 1;
        }
      }

      return b.id - a.id;
    });

    return sorted;
  }, [executions, executionStatusFilter, executionSort, normalizedSearch]);

  const executionSummaryById = useMemo(() => {
    const map = new Map<number, ExecutionResultSummary | null>();
    executions.forEach(execution => {
      map.set(execution.id, parseExecutionSummary(execution.ds_resultado_consulta));
    });
    return map;
  }, [executions]);

  const jobsExecutedValue = resolveJobsExecuted();

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setClockTick(Date.now());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (expandedExecutionId === null) {
      return;
    }

    const stillVisible = filteredExecutions.some(execution => execution.id === expandedExecutionId);
    if (!stillVisible) {
      setExpandedExecutionId(null);
    }
  }, [filteredExecutions, expandedExecutionId]);

  return (
    <div className="etl-bis-module">
      <div className="etl-bis-header-box">
        <div>
          <div className="etl-bis-header-title">
            <span className="etl-bis-header-icon">⚙️</span>
            <span className="etl-bis-header-text">ETL BIs</span>
          </div>
          <p className="etl-bis-subtitle">Orquestracao e monitoramento de cargas de dados para BI.</p>
        </div>
        <div className="etl-bis-header-actions">
          <span className="etl-bis-last-updated">
            Ultima atualizacao: {lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString('pt-BR') : '-'}
          </span>
          <button className="etl-bis-refresh-btn" onClick={carregarDados} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {message ? <p className={`etl-bis-message etl-bis-message-${messageType}`}>{message}</p> : null}

      <div className="etl-bis-status-grid">
        <div className="etl-bis-status-card etl-bis-status-card-neutral">
          <span>Total de Jobs</span>
          <strong>{status?.totalJobs ?? 0}</strong>
        </div>
        <div className="etl-bis-status-card etl-bis-status-card-running">
          <span>Em Execução</span>
          <strong>{status?.runningJobs ?? 0}</strong>
        </div>
        <div className="etl-bis-status-card etl-bis-status-card-paused">
          <span>Pausados</span>
          <strong>{status?.pausedJobs ?? 0}</strong>
        </div>
        <div className="etl-bis-status-card etl-bis-status-card-error">
          <span>Com Erro</span>
          <strong>{status?.jobsWithError ?? 0}</strong>
        </div>
        <div className="etl-bis-status-card etl-bis-status-card-success">
          <span>Executado</span>
          <strong>{jobsExecutedValue}</strong>
        </div>
        <div className="etl-bis-status-card etl-bis-status-card-delayed">
          <span>Em Atraso</span>
          <strong>{status?.jobsDelayed ?? 0}</strong>
        </div>
      </div>

      <div className="etl-bis-table-card">
        <div className="etl-bis-table-header">
          <h3>Jobs</h3>
          <button className="etl-bis-create-btn" onClick={abrirCriacaoJob}>
            Criar novo job
          </button>
        </div>

        {isFormOpen ? (
          <div className="etl-bis-form-card">
            <div className="etl-bis-form-header">
              <h4>{formMode === 'create' ? 'Criar novo job ETL' : `Editar job #${formJobId}`}</h4>
              <span>Configure nome, detalhes e conexoes de origem/destino.</span>
            </div>

            <div className="etl-bis-form-grid">
              <label>
                Nome do job
                <input
                  type="text"
                  className="etl-bis-search-input"
                  value={jobForm.nm_job}
                  onChange={event => alterarCampoForm('nm_job', event.target.value)}
                  placeholder="etl_novo_bi"
                />
              </label>
              <label>
                Cron
                <input
                  type="text"
                  className="etl-bis-search-input"
                  value={jobForm.cron_expr}
                  onChange={event => alterarCampoForm('cron_expr', event.target.value)}
                  placeholder="0 */2 * * *"
                />
              </label>
              <label>
                Tabela de destino
                <input
                  type="text"
                  className="etl-bis-search-input"
                  value={jobForm.nm_tabela_destino}
                  onChange={event => alterarCampoForm('nm_tabela_destino', event.target.value)}
                  placeholder="fato_etl_destino"
                />
              </label>
              <label>
                Limite carga inicial
                <input
                  type="number"
                  className="etl-bis-search-input"
                  value={jobForm.nr_limite_carga_inicial}
                  onChange={event => alterarLimiteCargaInicial(event.target.value)}
                  min={1}
                  max={10000}
                />
              </label>
              <label>
                Coluna origem (criacao)
                <input
                  type="text"
                  className="etl-bis-search-input"
                  value={jobForm.nm_coluna_dt_criacao_origem}
                  onChange={event => alterarCampoForm('nm_coluna_dt_criacao_origem', event.target.value)}
                  placeholder="dt_criacao"
                />
              </label>
              <label>
                Coluna origem (atualizacao)
                <input
                  type="text"
                  className="etl-bis-search-input"
                  value={jobForm.nm_coluna_dt_atualizacao_origem}
                  onChange={event => alterarCampoForm('nm_coluna_dt_atualizacao_origem', event.target.value)}
                  placeholder="dt_atualizacao"
                />
              </label>
              <label>
                Coluna destino (referencia)
                <input
                  type="text"
                  className="etl-bis-search-input"
                  value={jobForm.nm_coluna_dt_referencia_destino}
                  onChange={event => alterarCampoForm('nm_coluna_dt_referencia_destino', event.target.value)}
                  placeholder="dt_referencia"
                />
              </label>
              <label>
                Colunas chave (destino)
                <input
                  type="text"
                  className="etl-bis-search-input"
                  value={jobForm.colunas_chave_destino}
                  onChange={event => alterarCampoForm('colunas_chave_destino', event.target.value)}
                  placeholder="id, codigo, filial"
                />
              </label>
            </div>

            <label className="etl-bis-form-textarea-wrap">
              Detalhe do job
              <textarea
                className="etl-bis-form-textarea"
                value={jobForm.ds_job}
                onChange={event => alterarCampoForm('ds_job', event.target.value)}
                placeholder="Descreva o objetivo da carga ETL"
              />
            </label>

            <label className="etl-bis-form-textarea-wrap">
              Query de origem
              <textarea
                className="etl-bis-form-textarea etl-bis-form-textarea-query"
                value={jobForm.ds_query_origem}
                onChange={event => alterarCampoForm('ds_query_origem', event.target.value)}
                placeholder="SELECT colunas FROM tabela_origem WHERE ..."
              />
              <small className="etl-bis-query-hint">
                Dica: use ${'{'}var_ULT_ATUALIZACAO{'}'} na query para filtro incremental por data.
              </small>
              <small className="etl-bis-query-hint">
                Informe as colunas chave para fazer upsert e evitar registros duplicados no destino.
              </small>
            </label>

            <div className="etl-bis-metadata-actions">
              <button
                type="button"
                className="etl-bis-action-btn etl-bis-action-btn-edit"
                onClick={carregarColunasOrigem}
                disabled={sourceColumnsLoading}
              >
                {sourceColumnsLoading ? 'Lendo origem...' : 'Carregar colunas da query'}
              </button>
              <button
                type="button"
                className="etl-bis-action-btn etl-bis-action-btn-edit"
                onClick={carregarColunasDestino}
                disabled={destinationColumnsLoading}
              >
                {destinationColumnsLoading ? 'Lendo destino...' : 'Carregar colunas destino'}
              </button>
            </div>

            <div className="etl-bis-connection-blocks">
              <div className="etl-bis-connection-card">
                <div className="etl-bis-connection-header">
                  <h5>Banco de origem</h5>
                  <button
                    type="button"
                    className="etl-bis-test-connection-btn"
                    onClick={() => testarConexao('conexao_origem')}
                    disabled={testingConnectionSide !== null}
                  >
                    {testingConnectionSide === 'conexao_origem' ? 'Testando...' : 'Testar conexao'}
                  </button>
                </div>
                {connectionTestFeedback.conexao_origem.message ? (
                  <p className={`etl-bis-connection-feedback etl-bis-connection-feedback-${connectionTestFeedback.conexao_origem.type}`}>
                    {connectionTestFeedback.conexao_origem.message}
                  </p>
                ) : null}
                <div className="etl-bis-form-grid">
                  <label>
                    Tipo do banco
                    <select
                      className="etl-bis-select"
                      value={jobForm.conexao_origem.tipo}
                      onChange={event => alterarCampoConexao('conexao_origem', 'tipo', event.target.value)}
                    >
                      {DB_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Host
                    <input
                      type="text"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_origem.host}
                      onChange={event => alterarCampoConexao('conexao_origem', 'host', event.target.value)}
                      placeholder="192.168.0.10"
                    />
                  </label>
                  <label>
                    Porta
                    <input
                      type="text"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_origem.port}
                      onChange={event => alterarCampoConexao('conexao_origem', 'port', event.target.value)}
                      placeholder="3306"
                    />
                  </label>
                  <label>
                    Database
                    <input
                      type="text"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_origem.database}
                      onChange={event => alterarCampoConexao('conexao_origem', 'database', event.target.value)}
                      placeholder="db_origem"
                    />
                  </label>
                  <label>
                    Usuario
                    <input
                      type="text"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_origem.user}
                      onChange={event => alterarCampoConexao('conexao_origem', 'user', event.target.value)}
                      placeholder="etl_user"
                    />
                  </label>
                  <label>
                    Senha
                    <input
                      type="password"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_origem.password}
                      onChange={event => alterarCampoConexao('conexao_origem', 'password', event.target.value)}
                      placeholder="********"
                    />
                  </label>
                </div>
              </div>

              <div className="etl-bis-connection-card">
                <div className="etl-bis-connection-header">
                  <h5>Banco de destino</h5>
                  <button
                    type="button"
                    className="etl-bis-test-connection-btn"
                    onClick={() => testarConexao('conexao_destino')}
                    disabled={testingConnectionSide !== null}
                  >
                    {testingConnectionSide === 'conexao_destino' ? 'Testando...' : 'Testar conexao'}
                  </button>
                </div>
                {connectionTestFeedback.conexao_destino.message ? (
                  <p className={`etl-bis-connection-feedback etl-bis-connection-feedback-${connectionTestFeedback.conexao_destino.type}`}>
                    {connectionTestFeedback.conexao_destino.message}
                  </p>
                ) : null}
                <label className="etl-bis-toggle-line">
                  <input
                    type="checkbox"
                    checked={jobForm.criar_tabela_destino_se_nao_existir}
                    onChange={event => alterarFlagCriacaoTabelaDestino(event.target.checked)}
                  />
                  <span>Criar tabela de destino automaticamente se nao existir</span>
                </label>
                <div className="etl-bis-form-grid">
                  <label>
                    Tipo do banco
                    <select
                      className="etl-bis-select"
                      value={jobForm.conexao_destino.tipo}
                      onChange={event => alterarCampoConexao('conexao_destino', 'tipo', event.target.value)}
                    >
                      {DB_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Host
                    <input
                      type="text"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_destino.host}
                      onChange={event => alterarCampoConexao('conexao_destino', 'host', event.target.value)}
                      placeholder="192.168.0.20"
                    />
                  </label>
                  <label>
                    Porta
                    <input
                      type="text"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_destino.port}
                      onChange={event => alterarCampoConexao('conexao_destino', 'port', event.target.value)}
                      placeholder="1433"
                    />
                  </label>
                  <label>
                    Database
                    <input
                      type="text"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_destino.database}
                      onChange={event => alterarCampoConexao('conexao_destino', 'database', event.target.value)}
                      placeholder="db_destino"
                    />
                  </label>
                  <label>
                    Usuario
                    <input
                      type="text"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_destino.user}
                      onChange={event => alterarCampoConexao('conexao_destino', 'user', event.target.value)}
                      placeholder="bi_user"
                    />
                  </label>
                  <label>
                    Senha
                    <input
                      type="password"
                      className="etl-bis-search-input"
                      value={jobForm.conexao_destino.password}
                      onChange={event => alterarCampoConexao('conexao_destino', 'password', event.target.value)}
                      placeholder="********"
                    />
                  </label>
                </div>
              </div>
            </div>

            {jobForm.mapeamento_colunas.length ? (
              <div className="etl-bis-mapping-card">
                <div className="etl-bis-mapping-header">
                  <h5>Mapeamento de colunas</h5>
                  <span>
                    Destino: {destinationTableExists === null ? '-' : destinationTableExists ? 'Tabela encontrada' : 'Tabela nao encontrada'}
                    {destinationColumns.length ? ` (${destinationColumns.length} colunas)` : ''}
                  </span>
                </div>
                <div className="etl-bis-table-wrap">
                  <table className="etl-bis-table etl-bis-mapping-table">
                    <thead>
                      <tr>
                        <th>Coluna origem</th>
                        <th>Tipo origem</th>
                        <th>Manter nome</th>
                        <th>Coluna destino</th>
                        <th>Tipo destino</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobForm.mapeamento_colunas.map((mapping, index) => (
                        <tr key={`${mapping.sourceColumn}-${index}`}>
                          <td>{mapping.sourceColumn}</td>
                          <td>{mapping.sourceType || '-'}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={mapping.keepName}
                              onChange={event => atualizarMapping(index, 'keepName', event.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="etl-bis-search-input"
                              value={mapping.destinationColumn}
                              disabled={mapping.keepName}
                              onChange={event => atualizarMapping(index, 'destinationColumn', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="etl-bis-search-input"
                              value={mapping.destinationType || mapping.inferredDestinationType || 'VARCHAR(255)'}
                              onChange={event => atualizarMapping(index, 'destinationType', event.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <div className="etl-bis-form-actions">
              <button
                type="button"
                className="etl-bis-action-btn etl-bis-action-btn-edit"
                onClick={fecharFormulario}
                disabled={formSubmitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="etl-bis-action-btn etl-bis-action-btn-run"
                onClick={salvarJob}
                disabled={formSubmitting}
              >
                {formSubmitting
                  ? 'Salvando...'
                  : formMode === 'create'
                    ? 'Salvar novo job'
                    : 'Salvar alteracoes'}
              </button>
            </div>
          </div>
        ) : null}

        <div className="etl-bis-table-tools">
          <input
            type="text"
            className="etl-bis-search-input"
            placeholder="Buscar job, descricao ou cron"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
          />
          <select
            className="etl-bis-select"
            value={jobSort}
            onChange={event => setJobSort(event.target.value as 'name_asc' | 'name_desc' | 'recent_execution' | 'delay_first')}
          >
            <option value="name_asc">Ordenar: Nome (A-Z)</option>
            <option value="name_desc">Ordenar: Nome (Z-A)</option>
            <option value="recent_execution">Ordenar: Ultima execucao</option>
            <option value="delay_first">Ordenar: Atraso primeiro</option>
          </select>
        </div>
        <div className="etl-bis-table-wrap">
          <table className="etl-bis-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Descrição</th>
                <th>Fluxo Execução</th>
                <th>Última Execução</th>
                <th>Status</th>
                <th>Próxima Execução</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map(job => {
                const lastJobExecution = lastExecutionByJob.get(job.id);
                const delayed = lastJobExecution ? isExecutionDelayed(lastJobExecution) : false;
                const isPaused = String(job.st_job || 'active').toLowerCase() === 'paused';
                const isChangingState = changingJobId === job.id;
                const lastExecutionAt = getExecutionTimestamp(lastJobExecution);
                const lastExecutionStatus = String(lastJobExecution?.st_execucao || 'pending').toLowerCase();
                const nextRunAt = getNextRunForDisplay(job.cron_expr, lastExecutionAt, new Date(clockTick));
                const nextRunCountdown = formatRemainingTime(nextRunAt, new Date(clockTick));

                return (
                <tr key={job.id}>
                  <td>
                    <div className="etl-bis-job-cell">
                      <span>{job.nm_job}</span>
                      {isPaused ? <span className="etl-bis-mini-badge etl-bis-mini-badge-paused">pausado</span> : null}
                      {delayed ? <span className="etl-bis-mini-badge etl-bis-mini-badge-delay">atrasado</span> : null}
                    </div>
                  </td>
                  <td>{job.ds_job}</td>
                  <td>{formatCronReadable(job.cron_expr)}</td>
                  <td>{formatDateTime(lastExecutionAt)}</td>
                  <td>
                    <span className={`etl-bis-status-pill etl-bis-status-${lastExecutionStatus}`}>
                      {lastExecutionStatus}
                    </span>
                  </td>
                  <td>
                    <div className="etl-bis-next-run-cell">
                      <div>{nextRunCountdown}</div>
                      <div className="etl-bis-next-run-time">{nextRunAt ? nextRunAt.toLocaleString('pt-BR') : '-'}</div>
                    </div>
                  </td>
                  <td className="etl-bis-actions-cell">
                    <div className="etl-bis-actions-inline">
                      <button
                        type="button"
                        className="etl-bis-action-btn etl-bis-action-btn-run"
                        onClick={() => executarJob(job.id)}
                        disabled={runningJobId === job.id || isChangingState || job.cd_ativo !== 1 || isPaused}
                      >
                        {runningJobId === job.id ? 'Executando...' : 'Executar'}
                      </button>
                      <button
                        type="button"
                        className="etl-bis-action-btn etl-bis-action-btn-edit"
                        onClick={() => abrirEdicaoJob(job)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className={`etl-bis-action-btn ${isPaused ? 'etl-bis-action-btn-edit' : 'etl-bis-action-btn-pause'}`}
                        onClick={() => pausarJob(job)}
                        disabled={isChangingState}
                      >
                        {isChangingState ? 'Aguarde...' : isPaused ? 'Retomar' : 'Pausar'}
                      </button>
                      <button
                        type="button"
                        className="etl-bis-action-btn etl-bis-action-btn-danger"
                        onClick={() => desativarJob(job)}
                        disabled={isChangingState}
                      >
                        Desativar
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })}
              {filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={7}>Nenhum job encontrado para os filtros selecionados.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="etl-bis-table-card">
        <div className="etl-bis-table-header">
          <h3>Ultimas execucoes</h3>
          <div className="etl-bis-table-count">{filteredExecutions.length} registro(s)</div>
        </div>
        <div className="etl-bis-table-tools">
          <select
            className="etl-bis-select"
            value={executionStatusFilter}
            onChange={event => setExecutionStatusFilter(event.target.value as 'all' | 'queued' | 'running' | 'success' | 'error')}
          >
            <option value="all">Status: Todos</option>
            <option value="queued">Status: queued</option>
            <option value="running">Status: running</option>
            <option value="success">Status: success</option>
            <option value="error">Status: error</option>
          </select>
          <select
            className="etl-bis-select"
            value={executionSort}
            onChange={event => setExecutionSort(event.target.value as 'recent' | 'oldest' | 'delay_first' | 'records_desc')}
          >
            <option value="recent">Ordenar: Mais recentes</option>
            <option value="oldest">Ordenar: Mais antigas</option>
            <option value="delay_first">Ordenar: Atraso primeiro</option>
            <option value="records_desc">Ordenar: Mais registros</option>
          </select>
        </div>
        <div className="etl-bis-table-wrap">
          <table className="etl-bis-table">
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>Job</th>
                <th>Status</th>
                <th>Inicio</th>
                <th>Fim</th>
                <th>Registros</th>
              </tr>
            </thead>
            <tbody>
              {filteredExecutions.map(execution => {
                const isExpanded = expandedExecutionId === execution.id;
                const summary = executionSummaryById.get(execution.id) || null;
                const summaryColumns = Array.isArray(summary?.colunas) ? summary?.colunas || [] : [];
                const filtrosAplicados = summary?.filtrosAplicados || null;
                const registrosLidos = Number(execution.nr_registros_lidos ?? summary?.totalRegistrosLidos ?? 0);
                const registrosProcessados = Number(execution.nr_registros_processados ?? summary?.totalRegistrosProcessados ?? 0);
                const registrosInseridos = Number(execution.nr_registros_inseridos ?? summary?.totalRegistrosInseridos ?? 0);
                const registrosAtualizados = Number(execution.nr_registros_atualizados ?? summary?.totalRegistrosAtualizados ?? 0);
                const registrosRejeitados = Number(execution.nr_registros_rejeitados ?? summary?.totalRegistrosRejeitados ?? 0);
                const queryExecutada = String(execution.ds_query_executada || '').trim();

                return (
                  <React.Fragment key={execution.id}>
                    <tr
                      className={`etl-bis-execution-row ${isExpanded ? 'etl-bis-execution-row-expanded' : ''}`}
                      onClick={() => setExpandedExecutionId(isExpanded ? null : execution.id)}
                    >
                      <td>
                        <button
                          type="button"
                          className="etl-bis-expand-btn"
                          aria-label={isExpanded ? 'Ocultar detalhes da execucao' : 'Exibir detalhes da execucao'}
                          onClick={event => {
                            event.stopPropagation();
                            setExpandedExecutionId(isExpanded ? null : execution.id);
                          }}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                      </td>
                      <td>#{execution.id}</td>
                      <td>{execution.nm_job}</td>
                      <td>
                        <span className={`etl-bis-status-pill etl-bis-status-${String(execution.st_execucao).toLowerCase()}`}>
                          {execution.st_execucao}
                        </span>
                        {isExecutionDelayed(execution) ? <span className="etl-bis-mini-badge etl-bis-mini-badge-delay">atrasado</span> : null}
                      </td>
                      <td>{formatDateTime(execution.dt_inicio)}</td>
                      <td>{formatDateTime(execution.dt_fim)}</td>
                      <td>{registrosProcessados}</td>
                    </tr>

                    {isExpanded ? (
                      <tr className="etl-bis-execution-detail-row">
                        <td colSpan={7}>
                          <div className="etl-bis-execution-detail-grid">
                            <div className="etl-bis-execution-summary-block">
                              <h4>Resumo da execucao</h4>
                              <div className="etl-bis-execution-metrics">
                                <div className="etl-bis-execution-metric-card">
                                  <span>Registros lidos</span>
                                  <strong>{registrosLidos}</strong>
                                </div>
                                <div className="etl-bis-execution-metric-card">
                                  <span>Registros inseridos</span>
                                  <strong>{registrosInseridos}</strong>
                                </div>
                                <div className="etl-bis-execution-metric-card">
                                  <span>Registros atualizados</span>
                                  <strong>{registrosAtualizados}</strong>
                                </div>
                                <div className="etl-bis-execution-metric-card etl-bis-execution-metric-card-error">
                                  <span>Registros rejeitados</span>
                                  <strong>{registrosRejeitados}</strong>
                                </div>
                              </div>
                            </div>

                            <div className="etl-bis-execution-result-block">
                              <h4>Resultado da consulta</h4>
                              {summaryColumns.length > 0 ? (
                                <div className="etl-bis-table-wrap">
                                  <table className="etl-bis-table etl-bis-execution-columns-table">
                                    <thead>
                                      <tr>
                                        <th>Coluna</th>
                                        <th>Tipo</th>
                                        <th>Contagem</th>
                                        <th>Nulos</th>
                                        <th>Exemplo</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {summaryColumns.map(column => (
                                        <tr key={column.coluna}>
                                          <td>{column.coluna}</td>
                                          <td>{column.tipo}</td>
                                          <td>{column.contagem}</td>
                                          <td>{column.nulos}</td>
                                          <td>{column.exemplo || '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="etl-bis-execution-placeholder">Nao ha detalhamento de colunas para esta execucao.</p>
                              )}
                            </div>

                            <div className="etl-bis-execution-query-block">
                              <h4>Query executada</h4>
                              {queryExecutada ? (
                                <pre className="etl-bis-execution-query-text">{queryExecutada}</pre>
                              ) : (
                                <p className="etl-bis-execution-placeholder">Query executada nao registrada.</p>
                              )}

                              <h4>Filtros aplicados</h4>
                              {filtrosAplicados ? (
                                <div className="etl-bis-table-wrap">
                                  <table className="etl-bis-table etl-bis-execution-columns-table">
                                    <thead>
                                      <tr>
                                        <th>Filtro</th>
                                        <th>Valor aplicado</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td>var_ULT_ATUALIZACAO</td>
                                        <td>{filtrosAplicados.var_ULT_ATUALIZACAO || '-'}</td>
                                      </tr>
                                      <tr>
                                        <td>Possui watermark no destino</td>
                                        <td>{filtrosAplicados.possuiWatermarkDestino ? 'Sim' : 'Nao'}</td>
                                      </tr>
                                      <tr>
                                        <td>Coluna origem (criacao)</td>
                                        <td>{filtrosAplicados.colunaCriacaoOrigem || '-'}</td>
                                      </tr>
                                      <tr>
                                        <td>Coluna origem (atualizacao)</td>
                                        <td>{filtrosAplicados.colunaAtualizacaoOrigem || '-'}</td>
                                      </tr>
                                      <tr>
                                        <td>Coluna referencia destino</td>
                                        <td>{filtrosAplicados.colunaReferenciaDestino || '-'}</td>
                                      </tr>
                                      <tr>
                                        <td>Tabela destino</td>
                                        <td>{filtrosAplicados.tabelaDestino || '-'}</td>
                                      </tr>
                                      <tr>
                                        <td>Limite carga inicial</td>
                                        <td>{String(filtrosAplicados.limiteCargaInicial ?? '-')}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="etl-bis-execution-placeholder">Filtros aplicados nao registrados nesta execucao.</p>
                              )}
                            </div>

                            {String(execution.st_execucao).toLowerCase() === 'error' ? (
                              <div className="etl-bis-execution-error-block">
                                <h4>Motivo do erro</h4>
                                <p>{execution.ds_erro || 'Erro nao informado.'}</p>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
              {filteredExecutions.length === 0 ? (
                <tr>
                  <td colSpan={7}>Nenhuma execucao encontrada para os filtros selecionados.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EtlBisModule;
